import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../utils/sentry';
import { Sixy } from './Sixy';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const SIXY_ERROR_LINES = [
  'Algo travou nos meus circuitos. Foi mal pelo susto.',
  'Eita, esse bug pegou até a Sixy desprevenida. Vamos recarregar?',
  'Erro detectado. Já avisei meus criadores via Sentry. Aperta aí pra recomeçar?',
  'Putz, um neurônio meu pifou. Bora dar um restart?',
];

/**
 * Captura erros de render em qualquer parte da árvore React e mostra um fallback amigável
 * em vez de tela branca. Em produção, registra no console e envia pro Sentry.
 *
 * Visual:
 *  - SixyAvatar mood='error' (olhos vermelhos)
 *  - Frase random da Sixy reagindo ao erro
 *  - Botão "Voltar pro início" — força reload limpo
 *  - Detalhes técnicos só em DEV (collapsible)
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };
  private sixyLine = SIXY_ERROR_LINES[Math.floor(Math.random() * SIXY_ERROR_LINES.length)];

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Six Pass] Erro não tratado:', error, errorInfo);
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="min-h-screen min-h-[100svh] w-full bg-[#030308] text-slate-100 flex items-center justify-center p-6 overflow-hidden relative"
        >
          {/* Background ambient — glow vermelho sutil pra dar tom de erro sem desespero */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at center, rgba(239, 68, 68, 0.10) 0%, transparent 55%)',
            }}
          />

          <div className="relative w-full max-w-md text-center space-y-6">
            {/* Sixy modo erro — olhos vermelhos, linha reta de boca */}
            <div className="flex justify-center">
              <Sixy form="cloud" size="xl" mood="error" lookAt={0} />
            </div>

            {/* Speech bubble */}
            <div className="mx-auto max-w-xs">
              <div className="rounded-2xl border border-red-400/30 bg-red-500/[0.08] px-4 py-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-red-300/80 mb-1">
                  Sixy diz
                </p>
                <p className="text-sm text-slate-200 leading-relaxed">{this.sixyLine}</p>
              </div>
            </div>

            <h1 className="text-xl font-black">Algo deu errado</h1>
            <p className="text-slate-400 text-[13px] leading-relaxed">
              Tivemos um problema ao carregar esta tela. Pode tentar voltar pro início — se
              persistir, fala com a gente.
            </p>

            {import.meta.env.DEV && (
              <details className="text-left bg-slate-900/50 rounded-lg p-3 text-xs text-rose-300 overflow-auto max-h-40">
                <summary className="cursor-pointer text-slate-300 mb-2">
                  Detalhes técnicos (só DEV)
                </summary>
                <pre className="whitespace-pre-wrap break-words">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <button
              type="button"
              onClick={this.handleReload}
              className="w-full rounded-2xl bg-gradient-to-r from-pink-500 to-blue-500 hover:scale-[1.02] active:scale-[0.98] text-white font-bold py-3 transition-transform"
            >
              Voltar para o início
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
